import React from 'react';
import ReactGA from 'react-ga4';

import { CookieEnum } from 'constants/Cookies';
import useLocalUserPreferences, { USER_PREFERENCES } from 'hooks/useLocalUserPreferences';
import type { MayBe } from 'modules/global/typing/common/common';

export interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

const trackingId = process.env.REACT_APP_GOOGLE_TRACKING_ID;

const useAnalytics = () => {
  const { [USER_PREFERENCES.COOKIES]: cookiesPreferences } = useLocalUserPreferences();

  const hasConsent = cookiesPreferences?.[CookieEnum.ANALYTICS] ?? false;

  const trackEvent = React.useCallback((analytics: MayBe<AnalyticsEvent>) => {
    if (hasConsent && analytics?.category && analytics?.action) {
      ReactGA.event(analytics);
    }
  }, [hasConsent]);

  const onClick = React.useCallback(<T>(analytics: MayBe<AnalyticsEvent>, callback?: T) =>
    (...args: unknown[]) => {
      if (typeof callback === 'function') {
        callback(...args);
      }
      trackEvent(analytics);
    }, [trackEvent]);

  React.useEffect(() => {
    if (hasConsent && trackingId) {
      // Doesn't matter if we initialize multiple times because the library will check if it's already initialized
      ReactGA.initialize(trackingId, {
        debug: false,
        titleCase: false
      } as any);
      (window as any)[`ga-disable-${trackingId}`] = false;
    } else {
      // See https://developers.google.com/analytics/devguides/collection/gtagjs/user-opt-out
      (window as any)[`ga-disable-${trackingId}`] = true;
    }
  }, [hasConsent]);

  return React.useMemo(() => ({
    onClick,
    trackEvent,
    ga: ReactGA
  }), [onClick, trackEvent]);
};

export default useAnalytics;
